import React, { Suspense, lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { Spin } from 'antd'
import AdminLayout from '@/layouts/AdminLayout'

// Lazy load pages
const CloudControlPage = lazy(() => import('@/pages/cloud-control'))
const Login = lazy(() => import('@/pages/login'))
const MerchantDashboard = lazy(() => import('@/pages/merchant-dashboard'))
const Dashboard = lazy(() => import('@/pages/dashboard'))
const UserPage = lazy(() => import('@/pages/system/user'))
const RolePage = lazy(() => import('@/pages/system/role'))
const MenuPage = lazy(() => import('@/pages/system/menu'))
const LogPage = lazy(() => import('@/pages/system/log'))
const CachePage = lazy(() => import('@/pages/system/cache'))
const SpuList = lazy(() => import('@/pages/product/spu'))
const SpuEdit = lazy(() => import('@/pages/product/spu/edit'))
const ProductImportPage = lazy(() => import('@/pages/product/import'))
const CategoryPage = lazy(() => import('@/pages/product/category'))
const BrandPage = lazy(() => import('@/pages/product/brand'))
const AttrPage = lazy(() => import('@/pages/product/attr'))
const MerchantPage = lazy(() => import('@/pages/merchant'))
const LogisticsPage = lazy(() => import('@/pages/logistics/retired'))
const PaymentPage = lazy(() => import('@/pages/payment'))
const I18nPage = lazy(() => import('@/pages/i18n'))
const ActivationCodePage = lazy(() => import('@/pages/activation-code'))
const EncryptConfigPage = lazy(() => import('@/pages/system/encrypt-config'))
const EmailConfigPage = lazy(() => import('@/pages/system/email-config'))
const BankWithdrawalSettingsPage = lazy(() => import('@/pages/system/bank-withdrawal'))
const BenefitConfigPage = lazy(() => import('@/pages/system/benefit-config'))
const WithdrawNoticePage = lazy(() => import('@/pages/system/withdraw-notice'))
const RegisterConfigPage = lazy(() => import('@/pages/system/register-config'))
const SessionPage = lazy(() => import('@/pages/system/session'))
const TestMerchantPage = lazy(() => import('@/pages/system/test-merchant'))
const ShopOwnerPage = lazy(() => import('@/pages/distribution/shop-owner'))
const SignInConfigPage = lazy(() => import('@/pages/system/signin-config'))
const BoostRewardConfigPage = lazy(() => import('@/pages/system/boost-reward-config'))
const TeamOrderProfitConfigPage = lazy(() => import('@/pages/system/team-reward-config'))
const TeamOverviewPage = lazy(() => import('@/pages/merchant/team-overview'))
const FundAdjustmentsPage = lazy(() => import('@/pages/merchant/fund-adjustments'))
const MerchantBillPage = lazy(() => import('@/pages/merchant/bill'))
const MerchantPointsPage = lazy(() => import('@/pages/merchant/points'))
const ServerLinePage = lazy(() => import('@/pages/system/server-line'))
const OtaVersionPage = lazy(() => import('@/pages/system/ota-version'))
const SplashPage = lazy(() => import('@/pages/system/splash'))
const ProtocolPage = lazy(() => import('@/pages/system/protocol'))
const IdVerifyConfigPage = lazy(() => import('@/pages/system/id-verify-config'))
const SalesStepPage = lazy(() => import('@/pages/system/sales-step'))
const WarmTipPage = lazy(() => import('@/pages/system/warm-tip'))
const TaskConfigPage = lazy(() => import('@/pages/system/task-config'))
const ShopOwnerPromoPage = lazy(() => import('@/pages/distribution/shop-owner-promo'))
const TeamGradeRewardPage = lazy(() => import('@/pages/distribution/team-grade-reward'))
const WithdrawalPage = lazy(() => import('@/pages/distribution/withdrawal'))
const PersonalSlotPage = lazy(() => import('@/pages/personal-slot'))
const ChatPage = lazy(() => import('@/pages/chat'))
const AdSlotPage = lazy(() => import('@/pages/ad'))
const CampaignConfigPage = lazy(() => import('@/pages/ad/campaign-config'))
const StreamConfigPage = lazy(() => import('@/pages/ad/stream-config'))
const CampaignReviewPage = lazy(() => import('@/pages/ad/campaign-review'))
const MerchantAdPage = lazy(() => import('@/pages/merchant-ad'))
const ProductStatsPage = lazy(() => import('@/pages/stats/product'))
const ShopStatsPage = lazy(() => import('@/pages/stats/shop'))
const MerchantDailyStatsPage = lazy(() => import('@/pages/stats/merchant-daily'))
const MerchantDailyReportPage = lazy(() => import('@/pages/stats/merchant-daily-report'))
const StarRatingPage = lazy(() => import('@/pages/star-rating'))
const MerchantStaffPage = lazy(() => import('@/pages/merchant/staff'))
const TronPaymentPage = lazy(() => import('@/pages/tron-payment'))
const TOTPSecurityPage = lazy(() => import('@/pages/system/totp-security'))
const OrderPage = lazy(() => import('@/pages/order'))
const LineKeyPage = lazy(() => import('@/pages/system/line-key'))
const RewardCodePage = lazy(() => import('@/pages/reward-code'))
const GoodsSectionPage = lazy(() => import('@/pages/goods-section'))
const RecommendSectionPage = lazy(() => import('@/pages/recommend-section'))
const SearchKeywordsPage = lazy(() => import('@/pages/search-keywords'))
const GccRatioPage = lazy(() => import('@/pages/system/gcc-ratio'))
const MsgTemplatePage = lazy(() => import('@/pages/msg-template'))
const MerchantTaskListPage = lazy(() => import('@/pages/merchant-task'))
const MerchantTaskDetailPage = lazy(() => import('@/pages/merchant-task/detail'))
const CreateTaskPage = lazy(() => import('@/pages/merchant-task/create'))
const MerchantNotifyPage = lazy(() => import('@/pages/merchant-notify'))

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 300 }}>
    <Spin size="large" />
  </div>
)

const wrap = (Component: React.LazyExoticComponent<React.FC>) => (
  <Suspense fallback={<Loading />}><Component /></Suspense>
)

// Auth guard
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = localStorage.getItem('token')
  if (!token) return <Navigate to="/login" replace />
  return <>{children}</>
}

const router = createBrowserRouter([
  {
    path: '/login',
    element: wrap(Login),
  },
  {
    path: '/',
    element: <AuthGuard><AdminLayout /></AuthGuard>,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },
      { path: 'dashboard', element: wrap(Dashboard) },
      { path: 'cloud-control', element: wrap(CloudControlPage) },
      { path: 'merchant-dashboard', element: wrap(MerchantDashboard) },

      // 系统管理
      { path: 'system/config', element: <Navigate to="/system/register-config" replace /> },
      { path: 'system/user', element: wrap(UserPage) },
      { path: 'system/role', element: wrap(RolePage) },
      { path: 'system/menu', element: wrap(MenuPage) },
      { path: 'system/log', element: wrap(LogPage) },
      { path: 'system/cache', element: wrap(CachePage) },
      { path: 'system/encrypt-config', element: wrap(EncryptConfigPage) },
      { path: 'system/register-config', element: wrap(RegisterConfigPage) },
      { path: 'system/session', element: wrap(SessionPage) },
      { path: 'system/test-merchant', element: wrap(TestMerchantPage) },
      { path: 'system/signin-config', element: wrap(SignInConfigPage) },
      { path: 'system/boost-reward-config', element: wrap(BoostRewardConfigPage) },
      { path: 'system/team-reward-config', element: wrap(TeamOrderProfitConfigPage) },
      { path: 'system/server-line', element: wrap(ServerLinePage) },
      { path: 'system/ota-version', element: wrap(OtaVersionPage) },
      { path: 'system/splash', element: wrap(SplashPage) },
      { path: 'system/protocol', element: wrap(ProtocolPage) },
      { path: 'system/id-verify-config', element: wrap(IdVerifyConfigPage) },
      { path: 'system/sales-step', element: wrap(SalesStepPage) },
      { path: 'system/warm-tip', element: wrap(WarmTipPage) },
      { path: 'system/task-config', element: wrap(TaskConfigPage) },
      { path: 'system/email-config', element: wrap(EmailConfigPage) },
      { path: 'system/bank-withdrawal', element: wrap(BankWithdrawalSettingsPage) },
      { path: 'system/benefit-config', element: wrap(BenefitConfigPage) },
      { path: 'system/withdraw-notice', element: wrap(WithdrawNoticePage) },

      // 分销管理
      { path: 'distribution/shop-owner', element: wrap(ShopOwnerPage) },
      { path: 'distribution/shop-owner-promo', element: wrap(ShopOwnerPromoPage) },
      { path: 'distribution/team-grade-reward', element: wrap(TeamGradeRewardPage) },
      { path: 'distribution/withdrawal', element: wrap(WithdrawalPage) },

      // 商品管理
      { path: 'product/spu', element: wrap(SpuList) },
      { path: 'product/spu/create', element: wrap(SpuEdit) },
      { path: 'product/spu/edit/:id', element: wrap(SpuEdit) },
      { path: 'product/category', element: wrap(CategoryPage) },
      { path: 'product/brand', element: wrap(BrandPage) },
      { path: 'product/attr', element: wrap(AttrPage) },
      { path: 'product/import', element: wrap(ProductImportPage) },

      // 商品板块配置
      { path: 'goods-section', element: wrap(GoodsSectionPage) },

      // 推荐板块配置（热门/猜你喜欢）
      { path: 'recommend-section', element: wrap(RecommendSectionPage) },
      { path: 'search-keywords', element: wrap(SearchKeywordsPage) },

      // 商家管理
      { path: 'merchant', element: wrap(MerchantPage) },
      { path: 'merchant/staff', element: wrap(MerchantStaffPage) },
      { path: 'merchant/team-overview', element: wrap(TeamOverviewPage) },
      { path: 'merchant/fund-adjustments', element: wrap(FundAdjustmentsPage) },
      { path: 'merchant/bill', element: wrap(MerchantBillPage) },
      { path: 'merchant/points', element: wrap(MerchantPointsPage) },

      // 物流管理
      { path: 'logistics', element: wrap(LogisticsPage) },
      { path: 'logistics/freight', element: wrap(LogisticsPage) },
      { path: 'logistics/company', element: wrap(LogisticsPage) },

      // 支付管理
      { path: 'payment', element: wrap(PaymentPage) },

      // 多语言
      { path: 'i18n', element: wrap(I18nPage) },

      // 激活码管理
      { path: 'activation-code', element: wrap(ActivationCodePage) },

      // 广告管理（平台后台）
      { path: 'customer-service/chat', element: wrap(ChatPage) },
      { path: 'ad', element: wrap(AdSlotPage) },
      { path: 'ad/campaign-config', element: wrap(CampaignConfigPage) },
      { path: 'ad/stream-config', element: wrap(StreamConfigPage) },
      { path: 'ad/campaign-review', element: wrap(CampaignReviewPage) },

      // 商户推广管理
      { path: 'merchant-ad', element: wrap(MerchantAdPage) },

      // 统计分析
      { path: 'stats/product', element: wrap(ProductStatsPage) },
      { path: 'stats/shop', element: wrap(ShopStatsPage) },
      { path: 'stats/merchant-daily', element: wrap(MerchantDailyStatsPage) },
      { path: 'stats/merchant-daily-report', element: wrap(MerchantDailyReportPage) },

      // 展位激活码
      { path: 'personal-slot/codes', element: wrap(PersonalSlotPage) },

      // 商务星级评分
      { path: 'star-rating', element: wrap(StarRatingPage) },

      // TRON 支付管理
      { path: 'tron-payment', element: wrap(TronPaymentPage) },
      // TOTP 安全验证设置
      { path: 'totp-security', element: wrap(TOTPSecurityPage) },

      // 订单管理
      { path: 'order', element: wrap(OrderPage) },

      // 线路Key管理
      { path: 'system/line-key', element: wrap(LineKeyPage) },
      { path: 'reward-code', element: wrap(RewardCodePage) },
      { path: 'system/gcc-ratio', element: wrap(GccRatioPage) },

      // 运营：消息模板 / 商户群发任务 / 单发通知
      { path: 'msg-template', element: wrap(MsgTemplatePage) },
      { path: 'merchant-task', element: wrap(MerchantTaskListPage) },
      { path: 'merchant-task/create', element: wrap(CreateTaskPage) },
      { path: 'merchant-task/:id', element: wrap(MerchantTaskDetailPage) },
      { path: 'merchant-notify', element: wrap(MerchantNotifyPage) },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/dashboard" replace />,
  },
])

export default router
